import { useEffect, useState } from "react";
import { Link } from "react-router";
import { MessageSquare, FileText, Package, Wrench, TrendingUp, TrendingDown, Clock, CheckCircle, AlertCircle, ArrowUpRight, Loader2, FolderOpen } from "lucide-react";
import { servicesService } from "../../../lib/services/servicesService";
import { enquiriesService } from "../../../lib/services/enquiriesService";
import { blogService } from "../../../lib/services/blogService";
import { productsService } from "../../../lib/services/productsService";
import { projectsService } from "../../../lib/services/projectsService";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

export function AdminDashboard() {
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState({ enquiries: 0, blogs: 0, products: 0, services: 0, projects: 0 });
    const [recentEnquiries, setRecentEnquiries] = useState<any[]>([]);

    useEffect(() => {
        Promise.all([
            enquiriesService.list(),
            blogService.list(),
            productsService.list(),
            servicesService.list(),
            projectsService.list(),
        ]).then(([enqs, blogs, prods, svcs, projs]) => {
            setCounts({ enquiries: enqs.length, blogs: blogs.length, products: prods.length, services: svcs.length, projects: projs.length });
            setRecentEnquiries(enqs.slice(0, 5));
        }).catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const stats = [
        { label: "Total Enquiries", value: String(counts.enquiries), change: "From Appwrite DB", trend: "up", icon: MessageSquare },
        { label: "Blog Posts", value: String(counts.blogs), change: "All published", trend: "up", icon: FileText },
        { label: "Products Listed", value: String(counts.products), change: "All active", trend: "up", icon: Package },
        { label: "Services", value: String(counts.services), change: "All active", trend: "up", icon: Wrench },
        { label: "Projects", value: String(counts.projects), change: "All existing", trend: "up", icon: FolderOpen },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24 min-h-[60vh]">
                <div className="flex flex-col items-center gap-4 text-muted-foreground">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm font-medium">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {stats.map((s) => (
                    <Card key={s.label} className="overflow-hidden shadow-none">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                                {s.label}
                            </CardTitle>
                            <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center text-muted-foreground shrink-0 border border-border/20">
                                <s.icon className="w-4 h-4 text-primary/70" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold tracking-tight text-foreground">{s.value}</div>
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-2">
                                {s.trend === "up"
                                    ? <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                                    : <TrendingDown className="w-3.5 h-3.5 text-destructive" />}
                                {s.change}
                            </p>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Recent Enquiries */}
                <Card className="lg:col-span-2 shadow-none">
                    <CardHeader className="flex flex-row items-center justify-between pb-4 border-b">
                        <div>
                            <CardTitle className="text-lg">Recent Enquiries</CardTitle>
                            <CardDescription>Latest customer requests requiring attention.</CardDescription>
                        </div>
                        <Link to="/admin/enquiries" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:text-primary/80 transition-colors">
                            View all <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-border/50">
                            {recentEnquiries.length === 0 ? (
                                <p className="px-6 py-10 text-sm text-muted-foreground text-center">No enquiries yet.</p>
                            ) : (
                                recentEnquiries.map((e) => (
                                    <div key={e.$id} className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors group">
                                        <div className="flex items-center gap-4">
                                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0 uppercase">
                                                {e.name.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{e.name}</p>
                                                <p className="text-xs text-muted-foreground mt-0.5">{e.service}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-xs text-muted-foreground hidden sm:flex items-center gap-1.5">
                                                <Clock className="w-3.5 h-3.5" /> {e.date}
                                            </span>
                                            {e.status === "new" ? (
                                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                    <AlertCircle className="w-3 h-3 mr-1" /> New
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    <CheckCircle className="w-3 h-3 mr-1" /> Replied
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Quick links */}
                <Card className="shadow-none">
                    <CardHeader className="pb-4 border-b">
                        <CardTitle className="text-lg">Quick Actions</CardTitle>
                        <CardDescription>Jump to management sections.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="divide-y divide-border/50">
                            {[
                                { label: "View all enquiries", to: "/admin/enquiries", icon: MessageSquare },
                                { label: "Manage blog posts", to: "/admin/blog", icon: FileText },
                                { label: "Update products", to: "/admin/products", icon: Package },
                                { label: "View services", to: "/admin/services", icon: Wrench },
                                { label: "Manage projects", to: "/admin/projects", icon: FolderOpen },
                            ].map((item) => (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    className="flex items-center gap-4 px-6 py-4 hover:bg-muted/30 transition-colors group"
                                >
                                    <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center group-hover:bg-primary transition-colors">
                                        <item.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary-foreground transition-colors" />
                                    </div>
                                    <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">{item.label}</span>
                                    <ArrowUpRight className="w-4 h-4 text-muted-foreground/40 ml-auto group-hover:text-primary transition-colors" />
                                </Link>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
